import React, { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Image uploader that POSTs to /api/upload/image and returns the URL.
 * Props:
 *   value: current URL (may be relative /api/uploads/... or absolute)
 *   onChange(url): callback when user uploads or clears
 *   recommendedSize: e.g. "1920×600" - shown as hint
 *   category: backend folder grouping ("hero", "services", "products", "gallery", ...)
 *   aspect: tailwind aspect class for preview ("aspect-video", "aspect-square", ...)
 *   testIdPrefix: data-testid prefix for the upload input
 */
export default function ImageUpload({ value, onChange, recommendedSize, category = "general", aspect = "aspect-video", testIdPrefix = "img-upload", lang = "ar" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const resolveUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    // Relative URL coming from backend like /api/uploads/...
    const base = API_BASE.replace(/\/api$/, "");
    return base + u;
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error(lang === "ar" ? "الحجم الأقصى 5 ميجا" : "Max 5MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success(lang === "ar" ? `تم الرفع (${r.data.width}×${r.data.height})` : `Uploaded (${r.data.width}×${r.data.height})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "ar" ? "فشل الرفع" : "Upload failed"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange("");

  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onFile} className="hidden" data-testid={`${testIdPrefix}-input`} />
      {value ? (
        <div className={`relative rounded-lg overflow-hidden border border-[var(--border)] ${aspect}`}>
          <img src={resolveUrl(value)} alt="" className="w-full h-full object-cover" />
          <button onClick={clear} type="button" className="absolute top-2 end-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80" data-testid={`${testIdPrefix}-clear`}>
            <X size={14} />
          </button>
          <button onClick={() => inputRef.current?.click()} type="button" disabled={busy} className="absolute bottom-2 end-2 px-2 py-1 rounded text-xs bg-black/60 text-white flex items-center gap-1 hover:bg-black/80">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {lang === "ar" ? "تغيير" : "Change"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testIdPrefix}-button`}
          className={`relative w-full ${aspect} border-2 border-dashed border-[var(--border)] rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[var(--gold)] hover:bg-[var(--gold)]/5 transition group`}
        >
          {busy ? <Loader2 size={28} className="animate-spin text-[var(--gold)]" /> : <ImageIcon size={28} className="text-[var(--text-muted)] group-hover:text-[var(--gold)]" />}
          <span className="text-sm font-medium text-[var(--text)]">
            {busy ? (lang === "ar" ? "جارٍ الرفع..." : "Uploading...") : (lang === "ar" ? "اضغط لرفع صورة" : "Click to upload image")}
          </span>
          {recommendedSize && (
            <span className="text-xs text-[var(--text-muted)]">
              {lang === "ar" ? "الأبعاد المُوصى بها:" : "Recommended size:"} <strong className="text-[var(--gold)]">{recommendedSize}</strong>
            </span>
          )}
          <span className="text-[10px] text-[var(--text-muted)]">PNG · JPG · WEBP · {lang === "ar" ? "أقصى" : "Max"} 5MB</span>
        </button>
      )}
    </div>
  );
}
